// Temporary bans: how long is left, and the durations the owner can pick.

import { mark } from "../i18n";

// A ban entry is { at, name, reason, until? }. `until` is a server timestamp
// (ms); a ban without one lasts until the owner lifts it.
export const banActive = (entry, now) => !!entry && (!entry.until || entry.until > now);

// The same test the database rules make (database.rules.json): a ban whose
// `until` is in the past no longer counts.
export const banExpired = (entry, now) => !!entry && !!entry.until && entry.until <= now;

// The ban history, newest first. Every ban the owner makes is kept in
// banHistory/{id} (and stays there after it ends or is lifted). Bans still
// stored in bans/users without a log entry are added to it as they are:
// automatic bans, and bans from before the history existed.
// `status`: "lifted" (the owner lifted it), "replaced" (a newer ban took its
// place), "expired" (its time ran out) or "active".
export function banHistory(log, current, now) {
  const rows = Object.entries(log || {}).map(([key, e]) => ({ key, ...e }));
  for (const [uid, e] of Object.entries(current || {})) {
    if (e && e.hid && log && log[e.hid]) continue;
    rows.push({ key: `current-${uid}`, uid, ...e });
  }
  const status = (r) => (r.liftedAt ? "lifted" : r.replacedAt ? "replaced" : banExpired(r, now) ? "expired" : "active");
  return rows.map((r) => ({ ...r, status: status(r) })).sort((a, b) => (b.at || 0) - (a.at || 0));
}

const DAYS = mark("{n}d");
const HOURS = mark("{n}h");
const MINUTES = mark("{n}m");
const SECONDS = mark("{n}s");

// "2d 3h", "1h 5m", "4m 20s", "35s": two units at most, in the player's
// language. `t` is the translator, called as t("{n}d", { n }).
export function formatLeft(ms, t) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const part = (key, n) => (n > 0 ? t(key, { n }) : null);
  const two = (a, b) => [a, b].filter(Boolean).join(" ");
  if (d) return two(part(DAYS, d), part(HOURS, h));
  if (h) return two(part(HOURS, h), part(MINUTES, m));
  if (m) return two(part(MINUTES, m), part(SECONDS, s));
  return t(SECONDS, { n: s });
}

// The big timer on the ban screen: whole days, and the rest as HH:MM:SS.
export function clockLeft(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const days = Math.floor(total / 86400);
  const pad = (n) => String(n).padStart(2, "0");
  const time = `${pad(Math.floor((total % 86400) / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
  return { days, time };
}

// For the owner's pages, which are in English only.
export const formatLeftEn = (ms) => formatLeft(ms, (key, v) => key.replace("{n}", v.n));

// The fixed choices in the ban window (label, minutes). null = permanent.
export const BAN_DURATIONS = [
  { label: "10 minutes", minutes: 10 },
  { label: "1 hour", minutes: 60 },
  { label: "6 hours", minutes: 6 * 60 },
  { label: "1 day", minutes: 24 * 60 },
  { label: "7 days", minutes: 7 * 24 * 60 },
  { label: "30 days", minutes: 30 * 24 * 60 },
  { label: "Permanent", minutes: null },
];

export const CUSTOM_UNITS = { minutes: 1, hours: 60, days: 24 * 60 };
// A custom ban can't be longer than this (a year): longer than that is "permanent".
export const MAX_BAN_MINUTES = 365 * 24 * 60;

// The minutes for a custom ban ("3", "hours"), or null if it isn't a sensible one.
export function customMinutes(amount, unit) {
  const n = Number(amount);
  const per = CUSTOM_UNITS[unit];
  if (!per || !Number.isFinite(n) || n <= 0) return null;
  const minutes = Math.round(n * per);
  return minutes >= 1 && minutes <= MAX_BAN_MINUTES ? minutes : null;
}
